import React, { useState, useEffect, useRef } from "react";
import { PostData } from "../../../service/postData";
import { sendNotification } from "../../../_notificationSettings/notificationPanel";
import validator from "validator";
import { useLocation } from "react-router-dom";
import FeatureSelector from "./FeatureSelector";

const MasterPlanAdd = () => {
  const location = useLocation();
  const [prodId, setProdId] = useState("");
  const [buttonTitle, setButtonTitle] = useState("Add New");
  const [isLoaded, setIsLoaded] = useState(false);
  const [countryArray, setCountryArray] = useState([]);

  const [planTitle, setPlanTitle] = useState("");
  const [planAmount, setPlanAmount] = useState("");
  const [setupAmount, setSetupAmount] = useState("");
  const [countryId, setCountryId] = useState("");
  const [planId, setPlanId] = useState(0);
  const [planType, setPlanType] = useState("day");
  const [selectedFeatures, setSelectedFeatures] = useState([]);

  const [errors, setErrors] = useState({});
  const editorRef = useRef(null);

  // Get productId from query params
  useEffect(() => {
    const prod_id = new URLSearchParams(location.search).get("prod_id");
    setProdId(prod_id || "");
  }, [location.search]);

  // Fetch plan details & countries
  useEffect(() => {
    const fetchData = async () => {
      if (prodId) {
        try {
          const res = await fetch(`${global.API_STRIPEAPI_URL}getPlanDetails?productId=${prodId}`);
          const result = await res.json();

          if (res.ok) {
            setPlanId(result.stripe_product_id || 0);
            setPlanTitle(result.productName || "");
            const prices = result.prices?.[0] || {};
            setPlanAmount(prices.amount || "");
            setSetupAmount(prices.setup_amount || "");
            setCountryId(prices.country_id || "");
            setPlanType(prices.interval_type || "month");
            setButtonTitle(result.stripe_product_id ? "Update" : "Add New");
            setSelectedFeatures(prices.features || []);
          }
        } catch (err) {
          console.error("Error fetching plan details:", err);
        }
      }

      const countryData = await PostData(global.adminCountryList, "", "GET");
      setCountryArray(countryData.response || []);
      setIsLoaded(true);
    };
    fetchData();
  }, [prodId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let msg = "";

    if (name === "plan_title" && value.length < 5) msg = "*Title must be at least 5 characters";
    if ((name === "plan_amount" || name === "setup_amount") && !validator.isFloat(value)) msg = "*Enter numeric value";
    if ((name === "country_id" || name === "plan_type") && value === "") msg = "*Required";

    setErrors((prev) => ({ ...prev, [name]: msg }));

    if (name === "plan_title") setPlanTitle(value);
    if (name === "plan_amount") setPlanAmount(value);
    if (name === "setup_amount") setSetupAmount(value);
    if (name === "country_id") setCountryId(value);
    if (name === "plan_type") setPlanType(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const hasError = Object.values(errors).some((err) => err) ||
      !planTitle || !planAmount || !setupAmount || !countryId || !planType;

    if (hasError) {
      sendNotification("Validation Error", "Please fix errors before submitting", "danger");
      return;
    }

    if (planId) editSetupPlan();
    else addSetupPlan();
  };

  const addSetupPlan = async () => {
    const requestData = {
      productName: planTitle,
      amount: planAmount,
      setupAmount,
      countryId,
      interval: planType,
      features: selectedFeatures
    };

    try {
      const res = await fetch(`${global.API_STRIPEAPI_URL}createPlan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData)
      });
      const result = await res.json();

      if (res.ok) {
        sendNotification("Success 👍", result.message, "success");
        setTimeout(() => {
          window.location.href = `${global.webUrl}administrator/master-plan-list/`;
        }, 1500);
      } else sendNotification("Error 😓", result.message, "danger");
    } catch {
      sendNotification("Network Error 🚨", "Could not create plan", "danger");
    }
  };

  const editSetupPlan = async () => {
    const requestData = {
      stripeProductId: planId,
      newProductName: planTitle,
      newAmount: planAmount,
      newSetupAmount: setupAmount,
      countryId,
      newInterval: planType,
      features: selectedFeatures
    };

    try {
      const res = await fetch(`${global.API_STRIPEAPI_URL}updatePlan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData)
      });
      const result = await res.json();

      if (res.ok) {
        sendNotification("Success 👍", result.message, "success");
        setTimeout(() => {
          window.location.href = `${global.webUrl}administrator/master-plan-list/`;
        }, 1500);
      } else sendNotification("Error 😓", result.message, "danger");
    } catch {
      sendNotification("Network Error 🚨", "Could not update plan", "danger");
    }
  };

  return (
    <div className="content">
      <h1 className="content-heading mb-4">{buttonTitle} Master Plan</h1>

      <form className="card p-4" onSubmit={handleSubmit}>
        {/* Country & Interval */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label>Registered Country *</label>
            <select name="country_id" value={countryId} onChange={handleChange} className="form-control">
              <option value="">Select Country</option>
              {countryArray.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {errors.country_id && <small className="text-danger">{errors.country_id}</small>}
          </div>

          <div className="col-md-6 mb-3">
            <label>Plan Interval *</label>
            <select name="plan_type" value={planType} onChange={handleChange} className="form-control">
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
            {errors.plan_type && <small className="text-danger">{errors.plan_type}</small>}
          </div>
        </div>

        {/* Title & Pricing */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label>Plan Title *</label>
            <input type="text" name="plan_title" value={planTitle} onChange={handleChange} className="form-control" />
            {errors.plan_title && <small className="text-danger">{errors.plan_title}</small>}
          </div>

          <div className="col-md-3 mb-3">
            <label>Subscription Amount *</label>
            <input type="text" name="plan_amount" value={planAmount} onChange={handleChange} className="form-control" />
            {errors.plan_amount && <small className="text-danger">{errors.plan_amount}</small>}
          </div>

          <div className="col-md-3 mb-3">
            <label>Setup Amount *</label>
            <input type="text" name="setup_amount" value={setupAmount} onChange={handleChange} className="form-control" />
            {errors.setup_amount && <small className="text-danger">{errors.setup_amount}</small>}
          </div>
        </div>

        {/* Features */}
        <div className="mb-3">
          <label>Plan Features</label>
          <FeatureSelector selectedFeatures={selectedFeatures} onChange={setSelectedFeatures} />
        </div>

        {/* Submit */}
        <div className="text-right">
          <button className="btn btn-primary" type="submit" disabled={Object.values(errors).some((e) => e)}>
            <i className="fa fa-check mr-2"></i>{buttonTitle} Plan
          </button>
        </div>
      </form>
    </div>
  );
};

export default MasterPlanAdd;